<template>
  <Dialog
    :visible.sync="localVisible"
    :style="{ width: dialogWidth, maxWidth: dialogMaxWidth }"
    header="Preview Struk"
    :modal="true"
    :closable="true"
    :draggable="false"
    :resizable="false"
    class="receipt-preview-dialog"
    @hide="handleClose"
  >
    <!-- Loading State -->
    <div v-if="loading" class="receipt-loading">
      <ProgressSpinner />
      <p>Memuat struk...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="receipt-error">
      <Message severity="error" :closable="false">
        <strong>Gagal memuat struk</strong>
        <p>{{ error }}</p>
      </Message>
      <div class="p-mt-3">
        <Button
          label="Coba Lagi"
          icon="pi pi-refresh"
          @click="loadReceipt"
          :loading="loading"
        />
      </div>
    </div>

    <!-- Receipt Content -->
    <div v-else-if="receiptData" class="receipt-preview-content">
      <ReceiptContent
        :receipt-data="receiptData"
        :gift-eligable="giftEligable"
      />
    </div>

    <template #footer>
      <Button
        label="Tutup"
        icon="pi pi-times"
        class="p-button-text"
        @click="handleClose"
        :disabled="printing"
      />
      <Button
        label="Print"
        icon="pi pi-print"
        @click="handlePrint"
        v-shortkey="['enter']"
        @shortkey="handlePrint"
        :loading="printing"
        :disabled="isPrintDisabled"
      />
    </template>
  </Dialog>
</template>

<script>
import receiptService from '@/services/receiptService'
import ReceiptContent from '@/components/receipt/ReceiptContent.vue'
import { RECEIPT_CONFIG } from '@/config/receipt'
import ProgressSpinner from 'primevue/progressspinner'

export default {
  name: 'ReceiptPreviewDialog',
  components: {
    ReceiptContent,
    ProgressSpinner
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    invoiceId: {
      type: [Number, String],
      default: null
    },
    receiptDataProp: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      localReceiptData: null,
      giftEligable: null,
      printing: false,
      dialogWidth: RECEIPT_CONFIG.DIALOG_WIDTH,
      dialogMaxWidth: RECEIPT_CONFIG.DIALOG_MAX_WIDTH,
      fetchingGiftEligable: false // Guard flag to prevent duplicate API calls
    }
  },
  computed: {
    receiptData() {
      // Always prefer localReceiptData if set, otherwise use receiptDataProp
      const data = this.localReceiptData || this.receiptDataProp
      return data
    },
    isPrintDisabled() {
      // Check if print button should be disabled
      if (this.error) return true
      if (!this.receiptData) return true
      if (!this.receiptData.invoice) return true

      // Check invoice ID from receipt data first
      const receiptInvoiceId = this.receiptData.invoice.id
      // Fallback to prop if receipt data doesn't have invoice.id
      const invoiceId =
        receiptInvoiceId !== null &&
        receiptInvoiceId !== undefined &&
        receiptInvoiceId !== ''
          ? receiptInvoiceId
          : this.invoiceId

      // Invoice ID must exist and be valid
      // Note: 0 is considered valid (could be a real invoice ID in some systems)
      if (invoiceId === null || invoiceId === undefined || invoiceId === '') {
        return true
      }

      return false
    },
    localVisible: {
      get() {
        return this.visible
      },
      set(value) {
        this.$emit('update:visible', value)
      }
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        // Debug logging
        if (process.env.NODE_ENV === 'development') {
          console.log('ReceiptPreviewDialog visible changed to true:', {
            receiptDataProp: this.receiptDataProp,
            invoiceId: this.invoiceId,
            hasReceiptDataProp: !!this.receiptDataProp,
            hasInvoiceId: !!this.invoiceId
          })
        }

        // Always check receiptDataProp first when dialog opens
        if (this.receiptDataProp) {
          // Use provided receipt data directly
          this.localReceiptData = this.receiptDataProp
          this.loading = false
          this.error = null

          // Debug logging
          if (process.env.NODE_ENV === 'development') {
            console.log('Using receiptDataProp:', {
              localReceiptData: this.localReceiptData,
              invoiceId:
                this.localReceiptData && this.localReceiptData.invoice
                  ? this.localReceiptData.invoice.id
                  : null
            })
          }

          if (
            this.localReceiptData &&
            this.localReceiptData.invoice &&
            !this.localReceiptData.invoice.uniqueCode
          ) {
            this.fetchGiftEligable()
          }
        } else if (this.invoiceId) {
          // Fetch receipt by invoice ID
          if (process.env.NODE_ENV === 'development') {
            console.log('Fetching receipt by invoiceId:', this.invoiceId)
          }
          this.loadReceipt()
        } else {
          // No data and no invoice ID - show error
          console.warn(
            'ReceiptPreviewDialog: No receiptDataProp and no invoiceId'
          )
          this.error = 'Data struk tidak tersedia'
          this.loading = false
        }
      } else if (!newVal) {
        this.reset()
      }
    },
    receiptDataProp(newVal) {
      // Update localReceiptData whenever receiptDataProp changes
      // This handles cases where prop is set before dialog becomes visible
      // Note: fetchGiftEligable() is handled by visible watcher when dialog opens
      if (process.env.NODE_ENV === 'development') {
        console.log('receiptDataProp watcher fired:', {
          newVal: newVal,
          visible: this.visible,
          hasInvoice: !!(newVal && newVal.invoice)
        })
      }

      if (newVal) {
        this.localReceiptData = newVal
        this.loading = false
        this.error = null

        // If dialog is already visible and we just updated the data,
        // fetchGiftEligable will be called by visible watcher logic
        // No need to call it here to avoid duplicate calls
      } else if (newVal === null && this.visible) {
        // If receiptDataProp is cleared while dialog is visible, reset
        this.localReceiptData = null
      }
    }
  },
  mounted() {
    // Handle case where dialog opens with data already set
    if (this.visible && this.receiptDataProp) {
      this.localReceiptData = this.receiptDataProp
      this.loading = false
      this.error = null
    }
  },
  methods: {
    async loadReceipt() {
      if (!this.invoiceId) {
        this.error = 'Invoice ID tidak ditemukan'
        this.loading = false
        return
      }

      this.loading = true
      this.error = null

      try {
        const receiptData = await receiptService.fetchReceipt(
          this.invoiceId,
          this.$http
        )

        // Ensure invoice.id is set if missing from API response
        // This can happen if the API doesn't include id in the invoice object
        if (receiptData && receiptData.invoice) {
          if (!receiptData.invoice.id && this.invoiceId) {
            receiptData.invoice.id = this.invoiceId
          }
        }

        // Ensure Vue detects the change by using $set if needed
        this.$set(this, 'localReceiptData', receiptData)
        this.error = null

        // Fetch gift eligable if invoice doesn't have uniqueCode
        if (
          this.localReceiptData &&
          this.localReceiptData.invoice &&
          !this.localReceiptData.invoice.uniqueCode
        ) {
          await this.fetchGiftEligable()
        }
      } catch (error) {
        this.error = error.message || 'Gagal memuat struk. Silakan coba lagi.'
        this.localReceiptData = null
      } finally {
        this.loading = false
      }
    },

    async fetchGiftEligable() {
      // Prevent duplicate calls - if already fetching, return early
      if (this.fetchingGiftEligable) {
        return
      }

      if (!this.receiptData.invoice || !this.receiptData.invoice.number) {
        return
      }

      this.fetchingGiftEligable = true
      try {
        const response = await this.$http.get(
          `${process.env.VUE_APP_API_URL}/invoice?invoice_number=${this.receiptData.invoice.number}`
        )

        if (response.status === 200) {
          this.giftEligable = response.data.data
        } else {
          this.giftEligable = null
        }
      } catch (error) {
        this.giftEligable = null
      } finally {
        this.fetchingGiftEligable = false
      }
    },

    async handlePrint() {
      const receiptDataToPrint = this.receiptData
      if (!receiptDataToPrint) {
        this.$toast.add({
          severity: 'error',
          summary: 'Data struk tidak tersedia',
          life: RECEIPT_CONFIG.TOAST_DURATION_MS
        })
        return
      }

      // Ensure invoice.id is set (should be set after loadReceipt, but double-check)
      if (!receiptDataToPrint.invoice) {
        this.$toast.add({
          severity: 'error',
          summary: 'Data invoice tidak tersedia',
          life: RECEIPT_CONFIG.TOAST_DURATION_MS
        })
        return
      }

      // Use invoice.id from receipt data, fallback to prop if missing
      const invoiceId =
        receiptDataToPrint.invoice.id !== null &&
        receiptDataToPrint.invoice.id !== undefined &&
        receiptDataToPrint.invoice.id !== ''
          ? receiptDataToPrint.invoice.id
          : this.invoiceId

      if (!invoiceId && invoiceId !== 0) {
        this.$toast.add({
          severity: 'error',
          summary: 'Invoice ID tidak tersedia',
          life: RECEIPT_CONFIG.TOAST_DURATION_MS
        })
        return
      }

      // Ensure invoice.id is set for printing (in case it was missing)
      if (!receiptDataToPrint.invoice.id && invoiceId) {
        receiptDataToPrint.invoice.id = invoiceId
      }

      this.printing = true

      try {
        await receiptService.printReceipt(
          receiptDataToPrint,
          this.$http,
          this.$socket
        )

        this.$toast.add({
          severity: 'success',
          summary: 'Struk berhasil dikirim ke printer',
          life: RECEIPT_CONFIG.TOAST_DURATION_MS
        })

        // Close dialog after successful print (optional)
        setTimeout(() => {
          this.handleClose()
        }, RECEIPT_CONFIG.CLOSE_DELAY_MS)
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Gagal mencetak struk',
          detail: error.message,
          life: RECEIPT_CONFIG.TOAST_DURATION_MS
        })
      } finally {
        this.printing = false
      }
    },

    handleClose() {
      this.$emit('close')
      this.reset()
    },

    reset() {
      this.localReceiptData = null
      this.giftEligable = null
      this.error = null
      this.loading = false
      this.printing = false
      this.fetchingGiftEligable = false // Reset guard flag when dialog closes
    }
  }
}
</script>

<style scoped lang="scss">
.receipt-preview-dialog {
  ::v-deep .p-dialog-content {
    padding: 1rem;
    max-height: 80vh;
    overflow-y: auto;
  }
}

.receipt-preview-content {
  font-family: Monospace;
}

.receipt-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;

  p {
    margin-top: 1rem;
    color: #6b7280;
  }
}

.receipt-error {
  padding: 1rem;
}
</style>
